import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from 'components/card';
import SearchIcon from "components/icons/SearchIcon";
import { MdEmail } from 'react-icons/md';
import { useDisclosure } from '@chakra-ui/react';
import CreateButton from "components/buttons/CreateButton";
import Pagination from "components/pagination/Pagination";
import DateRangePicker from "components/dataPicker";
import {
    getPaginatedPurchases,
    getPurchases,
    createPurchase,
    sendPurchaseToSupplier,
    exportPurchases,
    getPurchaseDashboard,
  } from "interfaces/purchases";
import { getSuppliers } from 'interfaces/supplier';
import { toast } from 'react-toastify';
import CreatePurchaseModal from './components/createPurchase';
import UpdatePurchaseModal from './components/updatePurchaseModal';

const PurchasesPage = () => {
    const navigate = useNavigate();
    const createModal = useDisclosure();

    // Filters / UI
    const [activeTab, setActiveTab] = useState(1);
    const [search, setSearch] = useState('');
    const [dateRange] = useState('');
    const [supplierFilter, setSupplierFilter] = useState('all');

    // Data
    const [purchaseOrders, setPurchaseOrders] = useState<any[]>([]);
    const [suppliers, setSuppliers] = useState<any[]>([]);
    const isSupplierAccepted = (po: any) =>
        Boolean(
          po.aceptedBySupplier
        );

    // Loading / creation
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [detailId, setDetailId] = useState<string | null>(null);
    const openDetail = (id: string) => setDetailId(id);
    const closeDetail = () => setDetailId(null);
    const [sendingEmailId, setSendingEmailId] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [exportRange, setExportRange] = useState(() => {
      const endDate = new Date();
      endDate.setHours(23, 59, 59, 999);
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 3);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate };
    });

    // Column filters
    const [colFilters, setColFilters] = useState<Record<string, string>>({});
    const setColFilter = (col: string, val: string) => setColFilters(f => ({ ...f, [col]: val }));

    // Pagination
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [total, setTotal] = useState(0);

    // KPI header (server-computed, covers ALL purchases, not just the current page)
    const [dashboard, setDashboard] = useState({
      ongoingSpend: 0,
      pendingOrdersCount: 0,
      yearSpend: 0,
      paidTotal: 0,
      remainingTotal: 0,
    });
    
    const searchTimerRef = useRef<any>(null);
    const requestIdRef = useRef(0);

    // Parse date range (format "dd/mm/yyyy - dd/mm/yyyy")
    const parseRange = (value: string) => {
      if (!value || !value.includes('-')) return null;
      const [partA, partB] = value.split('-').map(s => s.trim());
      const [da, ma, ya] = partA.split('/').map(Number);
      const [db, mb, yb] = partB.split('/').map(Number);
      const startDate = new Date(ya, ma - 1, da, 0, 0, 0, 0);
      const endDate = new Date(yb, mb - 1, db, 23, 59, 59, 999);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;
      return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
    };

    const statusParamForTab = (tab: number) => {
      if (tab === 1) return 'pending,partial'; // in progress: both pending and partial
      if (tab === 2) return 'received';    // received
      if (tab === 3) return 'litigious,partial';
      return 'pending,partial';
    };

    const getMagicToken = (po: any) => {
           if (!po) return null;
           if (po.magicLinkUuid) return po.magicLinkUuid;
           if (po.magicLinkToken) return po.magicLinkToken;
           if (po.magicToken) return po.magicToken;
           const url = po.magicLink || po.link || '';
           if (!url) return null;
           const parts = String(url).split('/');
           return parts[parts.length - 1] || null;
         };

    const handleSendEmail = async (po: any) => {
        const token = getMagicToken(po);
        if (!token) {
            toast.error('Aucun magic link disponible pour cet achat.');
            return;
        }
        const rowId = po._id || po.id;
        setSendingEmailId(rowId);
        const res = await sendPurchaseToSupplier(token);
        setSendingEmailId(null);
        if (res && !res.error) toast.success('Email envoyé au fournisseur.');
        else toast.error("Erreur lors de l’envoi de l’email.");
    };

    const handleExportPurchases = async () => {
      setIsExporting(true);
      try {
        const status = statusParamForTab(activeTab);
        const range = {
          startDate: exportRange.startDate.toISOString(),
          endDate: exportRange.endDate.toISOString(),
        };
        const blob = await exportPurchases(status, search.trim(), range);
        if (!blob) {
          toast.error("Erreur lors de l'export.");
          return;
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `achats-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } finally {
        setIsExporting(false);
      }
    };

    // Load suppliers
    useEffect(() => {
      (async () => {
        const res: any = await getSuppliers();
        setSuppliers(res?.docs || res?.data || res || []);
      })();
    }, []);

    const loadDashboard = useCallback(async () => {
      const res: any = await getPurchaseDashboard();
      if (!res) return;
      setDashboard({
        ongoingSpend: Number(res.ongoingSpend) || 0,
        pendingOrdersCount: Number(res.pendingOrdersCount) || 0,
        yearSpend: Number(res.yearSpend) || 0,
        paidTotal: Number(res.paidTotal) || 0,
        remainingTotal: Number(res.remainingTotal) || 0,
      });
    }, []);

    useEffect(() => {
      loadDashboard();
    }, [loadDashboard]);

    const loadPurchases = useCallback(async () => {
      // Every search keystroke fires a new request (see the effect below);
      // guard against an older, slower request overwriting a newer one.
      const requestId = ++requestIdRef.current;
      setLoading(true);
      const status = statusParamForTab(activeTab);
      const range = parseRange(dateRange);
      let fetched: any = null;

      try {
        fetched = await getPaginatedPurchases?.(page, limit, status, search.trim(), 'createdAt', range);
      } catch (e) {
        fetched = null;
      }

      if (!fetched) {
        try {
          const all: any = await getPurchases?.();
          const allDocs = Array.isArray(all) ? all : (all?.data || []);
          if (Array.isArray(allDocs)) {
            const allowedStatuses = status ? status.split(',').map((s: string) => s.trim()) : [];
            const docsFiltered = allDocs.filter((p: any) => !status || allowedStatuses.includes(p.status));
            fetched = { docs: docsFiltered, meta: { total: docsFiltered.length } };
          }
        } catch (e) {
          fetched = null;
        }
      }

      if (requestId !== requestIdRef.current) return; // a newer request superseded this one

      const docs = fetched?.data || fetched?.docs || fetched?.results || [];
      const supplierMap = new Map((suppliers || []).map((s: any) => [(s._id || s.id), s]));
      const enriched = docs.map((p: any) => {
        if (!p.supplier) {
          const s =
            supplierMap.get(p.supplierId) ||
            supplierMap.get(p.supplier_id);
          if (s) return { ...p, supplier: s };
        }
        return p;
      });

      setPurchaseOrders(enriched);
      setTotal(fetched?.meta?.total ?? fetched?.totalDocs ?? fetched?.total ?? enriched.length ?? 0);

      setLoading(false);
    }, [activeTab, page, limit, search, dateRange, suppliers]);

    useEffect(() => {
      loadPurchases();
    }, [loadPurchases]);

    // Debounce search
    const onSearchChange = (val: string) => {
      setSearch(val);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setPage(1);
        loadPurchases();
      }, 400);
    };

    const toggleTab = (tab: number) => {
      if (tab === activeTab) return;
      setActiveTab(tab);
      setPage(1);
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'received':
                return (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Reçu
                    </span>
                );
            case 'pending':
                return (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        En cours
                    </span>
                );
            case 'partial':
                return (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        Partiel
                    </span>
                );
            case 'litigious':
                return (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                        Litiges
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        N/A
                    </span>
                );
        }
    };

    const getSupplierAcceptedBadge = (po: any) => {
        const accepted = isSupplierAccepted(po);
        return accepted ? (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-400 text-white">
            Accepté
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            En attente
          </span>
        );
      };

      const getPaymentBadge = (po: any) => {
             const v = po?.pay ?? po?.paid ?? po?.paymentStatus;
             const isPaid = v === true || v === 'paid' || v === 'payé' || v === 'Paid';
             const paymentsArr = Array.isArray(po?.payments) ? po.payments : [];
             const totalPaid = paymentsArr.reduce((s: number, p: any) => s + Number(p.amount || 0), 0);
             if (isPaid) return (
               <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-400 text-white">
                 Payé
               </span>
             );
             if (totalPaid > 0) return (
               <div>
                 <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                   Partiel
                 </span>
                 <div className="text-[11px] text-gray-500 mt-0.5">
                   {totalPaid.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                 </div>
               </div>
             );
             return (
               <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                 En attente
               </span>
             );
           };

    const handleCreatePurchase = async (payload: any) => {
        setCreating(true);
        try {
          const created = await createPurchase(payload);
          if (created) {
            toast.success('Bon de commande créé');
            // Rechargement complet (garantit cohérence)
            await loadPurchases();
            await loadDashboard();
          }
          return created;
        } catch (e) {
          toast.error('Erreur création');
          return null;
        } finally {
          setCreating(false);
        }
    };

    const formatMoney = (v: any) => {
      const num = Number(v);
      if (isNaN(num)) return '—';
      return Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR'}).format(num);
    };

    // Compute real TTC from items using individual VAT rates
    const computeTTC = (po: any): number => {
      const items: any[] = Array.isArray(po?.items) ? po.items : [];
      if (items.length === 0) {
        // Fallback: totalPrice stored on document
        return Number(po?.totalPrice) || 0;
      }
      let ht = 0;
      let tva = 0;
      items.forEach((it: any) => {
        const qty = Number(it.quantity ?? it.qty ?? 0);
        const price = Number(it.price ?? it.unitPrice ?? 0);
        const lineHT = qty * price;
        const vatRate = Number(it.vat ?? it.vatPurchase ?? 0) / 100;
        ht += lineHT;
        tva += lineHT * (vatRate > 0 ? vatRate : 0);
      });
      // Delivery fees: add as-is only if NOT already distributed into item prices
      const fees = po?.feesDistributed ? 0 : Number(po?.deliveryFees ?? 0);
      return Math.round((ht + tva + (Number.isFinite(fees) ? fees : 0)) * 100) / 100;
    };

    const formatCreatedBy = (v: any) => {
             if (!v) return '—';
             if (typeof v === 'string') return v;
             return v.fullName || v.email || '—';
           };

// util: get supplier name from various shapes
const getSupplierName = (p: any) => {
    if (!p) return '—';
    const s = p.supplier;
    if (!s) return '—';
    if (typeof s === 'string') return s;
    return s.displayName || s.company || s.name || '—';
  };
  
  // util: get supplier id (string) if possible
  const getSupplierId = (p: any) => {
    if (!p) return null;
    const s = p.supplier;
    if (!s) return p.supplierId || p.supplier_id || null;
    if (typeof s === 'string') return s;
    return s._id || s.id || null;
  };

  // util: build a searchable index string for an order
  const buildSearchIndex = (p: any) => {
    const supplierName = (p.supplier && typeof p.supplier === 'object')
      ? (p.supplier.displayName || p.supplier.company || p.supplier.name || '')
      : (p.supplier || '');

    const purchaseNumber = p.purchaseNumber || p.purchaseId || p._id || '';

    // include items names & refs (if present) to help partial search
    const itemsText = (Array.isArray(p.items) ? p.items.map((it: any) =>
      `${it.name || it.productName || it.productId?.name || ''} ${it.productId?.ref || it.ref || ''}`) : []
    ).join(' ');

    const totalHT = (p.totalHT !== undefined && p.totalHT !== null)
      ? String(p.totalHT)
      : String((p.items || []).reduce((sum: number, it: any) => {
          const q = Number(it.quantity ?? it.qty ?? 0);
          const price = Number(it.price ?? it.unitPrice ?? 0);
          return sum + (q * price);
        }, 0));

    const status = p.status || '';

    return `${supplierName} ${purchaseNumber} ${itemsText} ${totalHT} ${status}`.toLowerCase();
  };

  const normalizedSearch = (search || '').toString().trim().toLowerCase();

  const filteredPO = purchaseOrders.filter((p: any) => {
        // 1) tab filter (status)
        if (activeTab === 1 && !(p.status === 'pending' || p.status === 'partial')) return false;
        if (activeTab === 2 && p.status !== 'received') return false;
        if (activeTab === 3 && !(p.status === 'litigious' || p.status === 'partial')) return false;

        // 2) supplier filter by id (if any)
        if (supplierFilter && supplierFilter !== 'all') {
          const sid = String(getSupplierId(p) ?? '');
          if (sid !== String(supplierFilter)) return false;
        }

        // 3) search: if empty => accept
        if (!normalizedSearch) return true;

        // split search into tokens and require each token to appear somewhere in the built index
        const tokens = normalizedSearch.split(/\s+/).filter(Boolean);
        const idx = buildSearchIndex(p);

        // try also a loose match on purchaseId fragments: allow searching "kit" to match "PO-MGKIT"
        const purchaseId = String(p.purchaseId || p.purchaseNumber || p._id || '').toLowerCase();

        return tokens.every((t) => {
          if (idx.includes(t)) return true;
          if (purchaseId.includes(t)) return true;
          const num = Number(t.replace(',', '.'));
          if (!Number.isNaN(num)) {
            const th = Number(p.totalHT ?? 0);
            const tp = Number(p.totalPrice ?? 0);
            if (Math.abs(th - num) < 0.0001 || Math.abs(tp - num) < 0.0001) return true;
          }
          return false;
        });
      }).filter((p: any) => {
        // Column filters
        const matchDate = (fieldVal: any, filterVal: string) => {
          if (!filterVal) return true;
          if (!fieldVal) return false;
          return new Date(fieldVal).toISOString().slice(0, 10) === filterVal;
        };
        if (colFilters['purchaseId'] && !String(p.purchaseId || p._id || '').toLowerCase().includes(colFilters['purchaseId'].toLowerCase())) return false;
        if (colFilters['supplier'] && !getSupplierName(p).toLowerCase().includes(colFilters['supplier'].toLowerCase())) return false;
        if (colFilters['orderDate'] && !matchDate(p.purchaseDate || p.createdAt, colFilters['orderDate'])) return false;
        if (colFilters['deliveryDate'] && !matchDate(p.deliveryPurchase, colFilters['deliveryDate'])) return false;
        if (colFilters['totalHT'] && !String(p.totalHT ?? '').includes(colFilters['totalHT'])) return false;
        if (colFilters['status'] && p.status !== colFilters['status']) return false;
        if (colFilters['payment']) {
          const isPaid = p.pay === true;
          const hasPartial = Array.isArray(p.payments) && p.payments.length > 0 && !isPaid;
          if (colFilters['payment'] === 'paid' && !isPaid) return false;
          if (colFilters['payment'] === 'partial' && !hasPartial) return false;
          if (colFilters['payment'] === 'pending' && (isPaid || hasPartial)) return false;
        }
        return true;
      });


        return (
            <>
              {/* KPI row */}
              <div className="mt-4 grid h-full w-full grid-cols-1 gap-5 xl:mt-3">
                <div className="z-0 col-span-12 grid h-full w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <Card extra="!p-4">
                    <div className="flex items-center">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-600">Dépenses en cours</p>
                        <p className="text-2xl font-bold text-navy-700 dark:text-white">
                          {formatMoney(dashboard.ongoingSpend)}
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card extra="!p-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Réglé / Restant</p>
                      <p className="text-lg font-bold text-emerald-600">
                        {formatMoney(dashboard.paidTotal)}
                      </p>
                      <p className="text-lg font-bold text-amber-600">
                        {formatMoney(dashboard.remainingTotal)}
                      </p>
                    </div>
                  </Card>

                  <Card extra="!p-4">
                    <div className="flex items-center">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-600">Dépenses de l'année</p>
                        <p className="text-2xl font-bold text-navy-700 dark:text-white">
                          {formatMoney(dashboard.yearSpend)}
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card extra="!p-4">
                    <div className="flex items-center">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-600">Commandes en cours</p>
                        <p className="text-2xl font-bold text-navy-700 dark:text-white">
                          {dashboard.pendingOrdersCount}
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
          
                {/* Main card */}
                <div className="col-span-12 h-full w-full rounded-[20px]">
                  <Card extra="pt-[10px] pl-[20px] pr-[20px] pb-[20px] border">
                    {/* Tabs (centered) */}
                    <div className="w-full flex items-center justify-between mb-4">
                      <div className="mx-auto flex w-full max-w-3xl gap-2 justify-center">
                        {[{id:1,label:'Achats en cours'}, {id:2,label:'Achats reçus'}, {id:3,label:'Litiges achats'}].map(tab => (
                          <div
                            key={tab.id}
                            onClick={() => toggleTab(tab.id)}
                            className={`flex items-center justify-center cursor-pointer rounded-t-[12px] px-4 py-2 min-w-[150px] text-center ${
                              activeTab === tab.id
                                ? 'border-b-[4px] border-[#0260CB] bg-[#7090B0]/[0.05]'
                                : 'bg-[#7090B0]/[0.12]'
                            }`}
                          >
                            <p className="text-sm font-medium text-navy-700 dark:text-white">
                              {tab.label}
                            </p>
                          </div>
                        ))}
                      </div>
          
                      <div className="flex items-end gap-2">
                        <DateRangePicker
                          label="Période export"
                          defaultStartDate={exportRange.startDate}
                          defaultEndDate={exportRange.endDate}
                          onChange={(range: any) => setExportRange({ startDate: range.startDate, endDate: range.endDate })}
                        />
                        <button
                          onClick={handleExportPurchases}
                          disabled={isExporting}
                          className="flex items-center gap-1.5 text-xs font-medium border border-toola-500 text-toola-500 rounded-lg px-3 py-1.5 hover:bg-brand-50 whitespace-nowrap disabled:opacity-50"
                        >
                          {isExporting ? 'Export…' : 'Exporter'}
                        </button>
                        <button
                          onClick={() => navigate('/suppliers/forecast')}
                          className="flex items-center gap-1.5 text-xs font-medium border border-toola-500 text-toola-500 rounded-lg px-3 py-1.5 hover:bg-brand-50 whitespace-nowrap"
                        >
                          Prévision achats
                        </button>
                      </div>
                    </div>
          
                    {/* Search + date + create button */}
                    <div className="mt-4 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3">
                      <div className="flex w-full gap-3">
                        <div className="flex h-[38px] items-center rounded-xl bg-lightPrimary text-sm text-gray-600 w-full lg:w-[60%]">
                          <SearchIcon />
                          <input
                            type="text"
                            placeholder="Recherche fournisseur, référence..."
                            value={search}
                            onChange={(e) => onSearchChange(e.target.value)}
                            className="block w-full rounded-full bg-lightPrimary text-sm text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                          />
                        </div>

                        <select
                            value={supplierFilter}
                            onChange={(e) => setSupplierFilter(e.target.value)}
                            className="hidden md:inline-flex rounded-xl border border-gray-300 px-3 py-2 text-sm bg-white"
                        >
                        <option value="all">Tous les fournisseurs</option>
                            {suppliers.map((s: any) => {
                            const id = s._id || s.id;
                            const name = s.displayName || s.company || s.name || '(sans nom)';
                                return (
                            <option key={id} value={String(id)}>
                                {name}
                            </option>
                            );
                        })}
                        </select>

                      </div>
          
                      <div className="w-full lg:w-auto flex justify-end">
                        <CreateButton
                          value="Créer un bon de commande"
                          extra="text-sm"
                          onClick={createModal.onOpen}
                        />
                        <CreatePurchaseModal
                          isOpen={createModal.isOpen}
                          onClose={createModal.onClose}
                          suppliers={suppliers}
                          onCreated={() => loadPurchases()}
                          createPurchase={handleCreatePurchase}
                        />
                      </div>
                    </div>
          
                    {/* Table */}
                    <div className="mt-5">
                      {loading ? (
                        <div className="flex justify-center py-12">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
                        </div>
                      ) : (
                        <>
                          {/* ── Desktop table (hidden on mobile) ── */}
                          <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-100">
                            <table className="w-full text-xs text-left text-gray-700 border-collapse">
                              <thead className="text-[10px] text-gray-500 uppercase bg-gray-50 border-b border-gray-100">
                                <tr>
                                  <th className="px-3 py-2 whitespace-nowrap">Référence</th>
                                  <th className="px-3 py-2 whitespace-nowrap">Fournisseur</th>
                                  <th className="px-3 py-2 whitespace-nowrap">Commande</th>
                                  <th className="px-3 py-2 whitespace-nowrap">Livraison</th>
                                  <th className="px-3 py-2 whitespace-nowrap">HT</th>
                                  <th className="px-3 py-2 whitespace-nowrap">TTC</th>
                                  <th className="px-3 py-2 whitespace-nowrap">Statut</th>
                                  <th className="px-3 py-2 whitespace-nowrap">Acceptation</th>
                                  <th className="px-3 py-2 whitespace-nowrap">Paiement</th>
                                  <th className="px-3 py-2 whitespace-nowrap text-right">Actions</th>
                                </tr>
                                {/* Filter row */}
                                <tr className="bg-white border-b border-gray-100">
                                  <th className="px-2 py-1">
                                    <input type="text" placeholder="Filtrer…" value={colFilters['purchaseId'] || ''} onChange={e => setColFilter('purchaseId', e.target.value)}
                                      className="w-full border border-gray-200 rounded px-1.5 py-0.5 text-[10px] font-normal normal-case outline-none focus:border-blue-400" />
                                  </th>
                                  <th className="px-2 py-1">
                                    <input type="text" placeholder="Filtrer…" value={colFilters['supplier'] || ''} onChange={e => setColFilter('supplier', e.target.value)}
                                      className="w-full border border-gray-200 rounded px-1.5 py-0.5 text-[10px] font-normal normal-case outline-none focus:border-blue-400" />
                                  </th>
                                  <th className="px-2 py-1">
                                    <input type="date" value={colFilters['orderDate'] || ''} onChange={e => setColFilter('orderDate', e.target.value)}
                                      className="w-full border border-gray-200 rounded px-1.5 py-0.5 text-[10px] font-normal normal-case outline-none focus:border-blue-400" />
                                  </th>
                                  <th className="px-2 py-1">
                                    <input type="date" value={colFilters['deliveryDate'] || ''} onChange={e => setColFilter('deliveryDate', e.target.value)}
                                      className="w-full border border-gray-200 rounded px-1.5 py-0.5 text-[10px] font-normal normal-case outline-none focus:border-blue-400" />
                                  </th>
                                  <th className="px-2 py-1">
                                    <input type="text" placeholder="Filtrer…" value={colFilters['totalHT'] || ''} onChange={e => setColFilter('totalHT', e.target.value)}
                                      className="w-full border border-gray-200 rounded px-1.5 py-0.5 text-[10px] font-normal normal-case outline-none focus:border-blue-400" />
                                  </th>
                                  <th className="px-2 py-1" />
                                  <th className="px-2 py-1">
                                    <select value={colFilters['status'] || ''} onChange={e => setColFilter('status', e.target.value)}
                                      className="w-full border border-gray-200 rounded px-1.5 py-0.5 text-[10px] font-normal normal-case outline-none focus:border-blue-400 bg-white">
                                      <option value="">Tous</option>
                                      <option value="pending">En cours</option>
                                      <option value="partial">Partiel</option>
                                      <option value="received">Reçu</option>
                                      <option value="litigious">Litige</option>
                                    </select>
                                  </th>
                                  <th className="px-2 py-1" />
                                  <th className="px-2 py-1">
                                    <select value={colFilters['payment'] || ''} onChange={e => setColFilter('payment', e.target.value)}
                                      className="w-full border border-gray-200 rounded px-1.5 py-0.5 text-[10px] font-normal normal-case outline-none focus:border-blue-400 bg-white">
                                      <option value="">Tous</option>
                                      <option value="paid">Payé</option>
                                      <option value="partial">Partiel</option>
                                      <option value="pending">En attente</option>
                                    </select>
                                  </th>
                                  <th className="px-2 py-1 text-right">
                                    {Object.values(colFilters).some(v => v) && (
                                      <button onClick={() => setColFilters({})} className="text-[10px] text-gray-400 hover:text-red-500 whitespace-nowrap normal-case font-normal">✕ Reset</button>
                                    )}
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-50">
                                {filteredPO.length === 0 ? (
                                  <tr>
                                    <td colSpan={10} className="text-center py-12 text-gray-400 text-sm">
                                      Aucun bon de commande trouvé
                                      {Object.values(colFilters).some(v => v) && (
                                        <button onClick={() => setColFilters({})} className="ml-2 text-blue-500 hover:underline">Effacer les filtres</button>
                                      )}
                                    </td>
                                  </tr>
                                ) : filteredPO.map((po: any) => {
                                  const purchaseId = po.purchaseId || po._id;
                                  const supplierName = getSupplierName(po);
                                  const orderDate = po.purchaseDate || po.createdAt;
                                  const deliveryPlanned = po.deliveryPurchase || null;
                                  const totalHT = formatMoney(po.totalHT || 0);
                                  const totalPrice = formatMoney(computeTTC(po));
                                  return (
                                    <tr
                                      key={purchaseId}
                                      className="bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                                      onClick={() => openDetail(po._id)}
                                    >
                                      <td className="px-3 py-2">
                                        <div className="font-medium text-gray-900 text-xs truncate max-w-[110px]">{purchaseId}</div>
                                        {(po.createdBy || po.created_by) && (
                                          <div className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[110px]">{formatCreatedBy(po.createdBy)}</div>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-gray-900 font-medium max-w-[130px]">
                                        <div className="truncate">{supplierName}</div>
                                      </td>
                                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                                        {orderDate ? new Date(orderDate).toLocaleDateString('fr-FR') : '—'}
                                      </td>
                                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                                        {deliveryPlanned ? new Date(deliveryPlanned).toLocaleDateString('fr-FR') : '—'}
                                      </td>
                                      <td className="px-3 py-2 font-medium whitespace-nowrap">{totalHT}</td>
                                      <td className="px-3 py-2 font-medium whitespace-nowrap">
                                        <div>{totalPrice}</div>
                                        {po.deliveryFees > 0 && (
                                          <div className={`text-[10px] font-normal mt-0.5 ${po.feesDistributed ? 'text-emerald-600' : 'text-gray-400'}`}>
                                            {po.feesDistributed
                                              ? `✓ Frais répartis (${formatMoney(po.deliveryFees)})`
                                              : `+ Frais livr. : ${formatMoney(po.deliveryFees)}`}
                                          </div>
                                        )}
                                      </td>
                                      <td className="px-3 py-2">{getStatusBadge(po.status)}</td>
                                      <td className="px-3 py-2">{getSupplierAcceptedBadge(po)}</td>
                                      <td className="px-3 py-2">{getPaymentBadge(po)}</td>
                                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center gap-1 justify-end">
                                          <button
                                            onClick={() => openDetail(po._id)}
                                            className="text-brand-500 hover:text-brand-600 text-xs font-medium px-2 py-1 rounded hover:bg-brand-50 whitespace-nowrap"
                                          >
                                            Voir
                                          </button>
                                          <button
                                            onClick={() => handleSendEmail(po)}
                                            className="p-1 rounded border border-gray-200 hover:bg-gray-50"
                                            title="Envoyer par email au fournisseur"
                                          >
                                            {sendingEmailId === (po._id || po.id) ? (
                                              <span className="text-[10px] text-gray-400">…</span>
                                            ) : (
                                              <MdEmail className="w-3.5 h-3.5 text-toola-500" />
                                            )}
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          {/* ── Mobile cards (hidden on desktop) ── */}
                          <div className="md:hidden space-y-3">
                            {/* Mobile filters */}
                            <div className="grid grid-cols-2 gap-2 mb-3">
                              <input type="text" placeholder="Référence…" value={colFilters['purchaseId'] || ''} onChange={e => setColFilter('purchaseId', e.target.value)}
                                className="border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-blue-400" />
                              <input type="text" placeholder="Fournisseur…" value={colFilters['supplier'] || ''} onChange={e => setColFilter('supplier', e.target.value)}
                                className="border border-gray-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-blue-400" />
                              <select value={colFilters['status'] || ''} onChange={e => setColFilter('status', e.target.value)}
                                className="border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white outline-none focus:border-blue-400">
                                <option value="">Tous les statuts</option>
                                <option value="pending">En cours</option>
                                <option value="partial">Partiel</option>
                                <option value="received">Reçu</option>
                                <option value="litigious">Litige</option>
                              </select>
                              <select value={colFilters['payment'] || ''} onChange={e => setColFilter('payment', e.target.value)}
                                className="border border-gray-200 rounded-lg px-3 py-2 text-xs bg-white outline-none focus:border-blue-400">
                                <option value="">Tous paiements</option>
                                <option value="paid">Payé</option>
                                <option value="partial">Partiel</option>
                                <option value="pending">En attente</option>
                              </select>
                              {Object.values(colFilters).some(v => v) && (
                                <button onClick={() => setColFilters({})} className="col-span-2 text-xs text-red-400 hover:text-red-600 text-center py-1">✕ Effacer les filtres</button>
                              )}
                            </div>
                            {filteredPO.length === 0 ? (
                              <div className="text-center py-10 text-gray-400 text-sm">Aucun bon de commande trouvé</div>
                            ) : filteredPO.map((po: any) => {
                              const purchaseId = po.purchaseId || po._id;
                              const supplierName = getSupplierName(po);
                              const orderDate = po.purchaseDate || po.createdAt;
                              const deliveryPlanned = po.deliveryPurchase || null;
                              return (
                                <div
                                  key={purchaseId}
                                  className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm cursor-pointer active:bg-gray-50"
                                  onClick={() => openDetail(po._id)}
                                >
                                  <div className="flex items-start justify-between gap-2 mb-2">
                                    <div>
                                      <div className="font-semibold text-gray-900 text-sm">{supplierName}</div>
                                      <div className="text-xs text-gray-400">{purchaseId}</div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                      {getStatusBadge(po.status)}
                                      {getPaymentBadge(po)}
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500 mt-2">
                                    <div><span className="text-gray-400">Commande :</span> {orderDate ? new Date(orderDate).toLocaleDateString('fr-FR') : '—'}</div>
                                    <div><span className="text-gray-400">Livraison :</span> {deliveryPlanned ? new Date(deliveryPlanned).toLocaleDateString('fr-FR') : '—'}</div>
                                    <div><span className="text-gray-400">HT :</span> <span className="font-medium text-gray-700">{formatMoney(po.totalHT || 0)}</span></div>
                                    <div><span className="text-gray-400">TTC :</span> <span className="font-medium text-gray-700">{formatMoney(computeTTC(po))}</span></div>
                                  </div>
                                  <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-gray-50" onClick={(e) => e.stopPropagation()}>
                                    <button onClick={() => openDetail(po._id)} className="text-xs text-brand-500 font-medium px-3 py-1.5 rounded-lg border border-brand-200">Voir</button>
                                    <button onClick={() => handleSendEmail(po)} className="p-1.5 rounded-lg border border-gray-200" title="Envoyer par email">
                                      {sendingEmailId === (po._id || po.id) ? <span className="text-[10px] text-gray-400">…</span> : <MdEmail className="w-4 h-4 text-toola-500" />}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Pagination */}
                          <Pagination
                            extra="mt-4"
                            maxItems={total}
                            itemsPerPage={[25, 50, 100]}
                            defaultItemsPerPage={limit}
                            page={page}
                            onChange={(newPage: number, newLimit: number) => {
                              setPage(newPage);
                              setLimit(newLimit);
                            }}
                          />
                        </>
                      )}
                    </div>
                  </Card>
                </div>
          
                {/* Detail modal */}
                {detailId && (
                  <UpdatePurchaseModal
                    id={detailId}
                    isOpen={!!detailId}
                    onClose={closeDetail}
                    onUpdated={() => { loadPurchases(); loadDashboard(); }}
                  />
                )}
              </div>
            </>
          );
          
};

export default PurchasesPage;